import Icon from '../../../components/AppIcon';

const CapabilitiesSection = () => {
  const capabilities = [
    {
      id: 1,
      icon: "Wrench",
      title: "Complex Installation Management",
      description: "End-to-end project management for large-scale commercial installations with multiple stakeholders and tight deadlines.",
      features: [
        "Multi-phase project planning",
        "Stakeholder coordination",
        "Quality assurance protocols",
        "Documentation & compliance"
      ]
    },
    {
      id: 2,
      icon: "Clock",
      title: "24/7 Emergency Response",
      description: "Dedicated commercial emergency services ensuring minimal downtime for your business operations.",
      features: [
        "Rapid response teams",
        "Priority service agreements",
        "Backup system deployment",
        "Real-time status updates"
      ]
    },
    {
      id: 3,
      icon: "Shield",
      title: "Preventive Maintenance Programs",
      description: "Customized maintenance schedules designed to prevent issues before they impact your operations.",
      features: [
        "Regular inspection schedules",
        "Predictive maintenance",
        "Equipment lifecycle management",
        "Cost optimization strategies"
      ]
    },
    {
      id: 4,
      icon: "Users",
      title: "Dedicated Account Management",
      description: "Single point of contact for all your commercial plumbing and electrical needs across multiple locations.",
      features: [
        "Personalized service plans",
        "Multi-location coordination",
        "Budget planning assistance",
        "Performance reporting"
      ]
    },
    {
      id: 5,
      icon: "FileCheck",
      title: "Compliance & Certification",
      description: "Ensuring all work meets or exceeds industry standards and regulatory requirements.",
      features: [
        "Code compliance verification",
        "Safety certifications",
        "Inspection coordination",
        "Documentation management"
      ]
    },
    {
      id: 6,
      icon: "TrendingUp",
      title: "Scalable Solutions",
      description: "Infrastructure designed to grow with your business, supporting expansion and increased capacity needs.",
      features: [
        "Future-proof design",
        "Modular systems",
        "Capacity planning",
        "Technology integration"
      ]
    }
  ];

  return (
    <section className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Zap" size={20} color="var(--color-primary)" />
            <span className="text-primary font-semibold text-sm font-accent">Core Capabilities</span>
          </div>
          <h2 className="font-headline font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            Enterprise-Grade Service Delivery
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto font-body">
            Comprehensive commercial services backed by three decades of proven expertise in managing complex projects for major brands.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {capabilities?.map((capability) => (
            <div
              key={capability?.id}
              className="bg-card rounded-xl shadow-card hover:shadow-elevated transition-all duration-300 p-6 group"
            >
              <div className="bg-primary/10 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <Icon name={capability?.icon} size={28} color="var(--color-primary)" className="group-hover:text-primary-foreground" />
              </div>

              <h3 className="font-headline font-bold text-xl md:text-2xl text-foreground mb-3">
                {capability?.title}
              </h3>

              <p className="text-muted-foreground mb-6 font-body leading-relaxed">
                {capability?.description}
              </p>

              <div className="space-y-2">
                {capability?.features?.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground font-body">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 lg:mt-16 bg-card rounded-2xl shadow-elevated p-8 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-headline font-bold text-2xl md:text-3xl text-foreground mb-4">
                Ready to Partner with Industry Experts?
              </h3>
              <p className="text-muted-foreground mb-6 font-body leading-relaxed">
                Join McDonald's, KFC, and other major brands who trust meriodine for their commercial infrastructure needs. Let's discuss how we can support your operations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:0827935785"
                  className="inline-flex items-center justify-center gap-3 bg-accent text-accent-foreground px-8 py-4 rounded-lg font-semibold text-lg hover:bg-accent/90 transition-all emergency-cta"
                >
                  <Icon name="Phone" size={24} />
                  <span className="font-accent">082 793 5785</span>
                </a>
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary/90 transition-all"
                >
                  <span>Request Consultation</span>
                  <Icon name="ArrowRight" size={20} />
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-6 text-center">
                <p className="text-4xl md:text-5xl font-bold text-primary mb-2 font-headline">500+</p>
                <p className="text-muted-foreground font-body">Commercial Projects</p>
              </div>
              <div className="bg-gradient-to-br from-success/10 to-accent/10 rounded-xl p-6 text-center">
                <p className="text-4xl md:text-5xl font-bold text-success mb-2 font-headline">30+</p>
                <p className="text-muted-foreground font-body">Years Experience</p>
              </div>
              <div className="bg-gradient-to-br from-accent/10 to-primary/10 rounded-xl p-6 text-center">
                <p className="text-4xl md:text-5xl font-bold text-accent mb-2 font-headline">24/7</p>
                <p className="text-muted-foreground font-body">Emergency Support</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CapabilitiesSection;